import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { Alert, Button, TextInput, useToast } from "@/ui";
import { SessionExpiredNotice } from "@/auth/SessionExpiredNotice";
import { AuthCard, AuthLayout } from "@/layout/AuthLayout";
import { useAuth } from "@/auth/AuthProvider";
import { homeFor } from "@/auth/RequireAuth";
import { useTenant } from "@/tenant/TenantProvider";
import { ApiClientError } from "@/lib/api";
import { InstallAppButton } from "@/components/InstallAppButton";

/** Staff sign-in for one organization: email + password (D-06). */
export function OrgStaffLogin() {
  const { orgSlug = "" } = useParams<{ orgSlug: string }>();
  const { login } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const { payload, error: tenantError, loading: tenantLoading } = useTenant();

  // Remember which org the user is signing in to — the PWA's `start_url`
  // is `/`, so when the installed app launches we redirect to the last
  // visited org's login (handled by PwaLaunchRedirect).
  useEffect(() => {
    if (orgSlug) localStorage.setItem("gatiq-last-org", orgSlug);
  }, [orgSlug]);

  const [loginId, setLoginId] = useState("");
  const [password, setPassword] = useState("");
  const [fields, setFields] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setFields({});
    setSubmitting(true);
    try {
      const user = await login(orgSlug, loginId, password);
      if ("twoFaRequired" in user && user.twoFaRequired) {
        navigate("/login/twofa", { replace: true });
        return;
      }
      navigate(user.mustChangePassword ? `/${orgSlug}/set-password` : homeFor(user), {
        replace: true,
      });
    } catch (err) {
      if (err instanceof ApiClientError) {
        setFields(err.fields ?? {});
        toast.error(err.message);
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  const brand = payload.branding;
  const orgName = payload.orgName ?? brand.appName ?? "";

  return (
    <AuthLayout
      accent="made simple."
      blurb={
        brand.tagline ||
        "A QR-based rewards platform that incentivises karigars through retail counters — scan, earn, and redeem with ease."
      }
      headline="Karigar rewards,"
      points={[
        "Register karigars straight from a counter QR code",
        "Approve new karigars in a couple of taps",
        "Everything branded as " + orgName,
      ]}
    >
      <AuthCard
        footer={
          <>
            Karigar?{" "}
            <Link className="font-medium text-brand-ink underline" to={`/${orgSlug}/login/karigar`}>
              Sign in with your mobile
            </Link>
          </>
        }
        subtitle="Staff sign in"
        title="Welcome back"
      >
        <SessionExpiredNotice />
        {tenantError && !tenantLoading ? (
          <Alert kind="danger">{tenantError}</Alert>
        ) : (
          <form className="flex flex-col gap-4" noValidate onSubmit={handleSubmit}>
            <TextInput
              autoComplete="username"
              autoFocus
              error={fields.email ?? fields.phone}
              label="Email or mobile number"
              onChange={(e) => setLoginId(e.target.value)}
              required
              value={loginId}
            />
            <TextInput
              autoComplete="current-password"
              error={fields.password}
              label="Password"
              onChange={(e) => setPassword(e.target.value)}
              required
              type="password"
              value={password}
            />
            <Link
              className="-mt-2 self-end text-sm font-medium text-brand-ink underline"
              to={`/${orgSlug}/forgot-password`}
            >
              Forgot password?
            </Link>
            <Button block loading={submitting} type="submit">
              Sign in
            </Button>
            <InstallAppButton label={"Install app for " + orgName} />
          </form>
        )}
      </AuthCard>
    </AuthLayout>
  );
}
